import './HeroLeaders.css';
import { useId, useLayoutEffect } from 'preact/hooks';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import { t } from '../../copy/strings';
import { fmtPct, fmtPctParts } from '../../format/numbers';
import type { Candidate, ResultsView } from '../../model/types';
import { openProfile } from '../../state/actions';
import { useConfig, useLayout } from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';

/** Um lado do herói: o `HeroSide` de `features/hero/model.ts` (§7.2) satisfaz esta forma. */
export interface LeaderSide {
  candidate: Candidate;
  /** Fração dos válidos (0–1). */
  share: number;
  votes: number;
}

export interface HeroLeadersProps {
  /** [número menor, número maior] (`HeroModel.sides`); a ordem nunca segue os votos (N01). */
  sides: readonly [LeaderSide, LeaderSide];
  /** Menos de 1% das seções (`HeroModel.early`): números e votos em --color-fg-3; o "%" fica na cor do partido. */
  early?: boolean;
  /** Primeiro snapshot dentro da janela `intro` (§7.2 bloco 5): % e votos contam de 90% a 100%. */
  intro?: boolean;
}

/**
 * Lados do herói a partir da vista (§7.2 `HeroModel.sides`, sem `leads`): `[candidates[0], candidates[1]]` com as fatias
 * e os votos de `national`, na ordem do contrato [número menor, número maior]; nunca reordenado pelo líder.
 */
export function heroSides(v: ResultsView): [LeaderSide, LeaderSide] {
  const n = v.national;
  return [
    { candidate: v.candidates[0], share: n.shares[0], votes: n.votes[0] },
    { candidate: v.candidates[1], share: n.shares[1], votes: n.votes[1] },
  ];
}

/** Marca de desempenho do primeiro número (§15.9.5): uma vez por documento. */
export const FIRST_NUMBER_MARK = 'hero:first-number';
let firstNumberMarked = false;

/**
 * `performance.mark('hero:first-number')` na primeira renderização de `HeroLeaders`, que só monta com números de um
 * snapshot aceito (o `HeroCard` a troca pelo `PreResultsGrid` sem votos). `useLayoutEffect`: o DOM já tem os números
 * e o quadro ainda não foi pintado. Só marca local, nada é enviado (§15.9.5).
 */
function markFirstNumber(): void {
  if (firstNumberMarked) return;
  firstNumberMarked = true;
  if (typeof performance !== 'undefined' && typeof performance.mark === 'function') performance.mark(FIRST_NUMBER_MARK);
}

/** Retrato do herói: 52 px em todos os modos (na TV o palco escala; §7.5 bloco 6). */
const PORTRAIT_PX = 52;

const pctValue = (v: number): string => fmtPctParts(v, 2).value;
const pctSr = (v: number): string => t('hero.leader.share.sr', { P: fmtPct(v, 2) });
const votesText = (v: number): string => {
  const n = Math.round(v);
  return t('hero.leader.votes', { N: n });
};

/**
 * Os dois candidatos do herói (§7.5): coluna 13 à esquerda e 22 à direita, mesmas classes nos dois lados (só a de
 * partido muda), espelhamento por posição (`:nth-child(2)`). Cada coluna é um `<button>` cujo nome é o conteúdo visível
 * e `hero.leader.aria` vai em `aria-describedby` (§15.4.2); na TV a coluna é um `<div>` (só leitura).
 */
export function HeroLeaders({ sides, early = false, intro = false }: HeroLeadersProps) {
  const photos = useConfig().features.photos;
  const clickable = useLayout().mode !== 'tv';
  const id = useId();
  useLayoutEffect(markFirstNumber, []);
  return (
    <div class="hero-leaders" data-testid="hero-leaders" data-state={early ? 'early' : 'value'}>
      {sides.map((side, i) => {
        const c = side.candidate;
        const party = partyOf(c);
        const unit = fmtPctParts(side.share, 2).unit;
        const descId = `${id}-d${i}`;
        const body = (
          <>
            <span class="hero-leaders__who">
              <span class="hero-leaders__portrait">
                <Portrait candidate={c} photo={photos ? c.photo : null} size={PORTRAIT_PX} loading="eager" />
              </span>
              <span class="hero-leaders__id">
                <span class="hero-leaders__name">{c.display_name}</span>
                <PartyTag candidate={c} />
              </span>
            </span>
            <span class="hero-leaders__fig" data-dataviz="">
              <AnimatedNumber
                value={side.share}
                format={pctValue}
                srFormat={pctSr}
                intro={intro}
                class="hero-leaders__pct"
              />
              <sup class="hero-leaders__unit" aria-hidden="true">
                {unit}
              </sup>
            </span>
            <span class="hero-leaders__votes">
              <AnimatedNumber value={side.votes} format={votesText} intro={intro} />
            </span>
          </>
        );
        const cls = `hero-leaders__side ${partyClass(party)}`;
        if (!clickable) {
          return (
            <div key={i} class={cls} data-number={c.number}>
              {body}
            </div>
          );
        }
        return (
          <button
            key={i}
            type="button"
            class={cls}
            data-number={c.number}
            aria-describedby={descId}
            onClick={() => openProfile(c.number, 'president', 'list')}
          >
            {body}
            <span id={descId} hidden>
              {t('hero.leader.aria', { NOME: c.display_name, SIGLA: party.display, NUM: c.number })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
